import React, { useState, useEffect, useRef } from "react";
import HTMLFlipBook from "react-pageflip";
import Zoom from "./Zoom";
import "@fortawesome/fontawesome-free/css/all.css";

// Utility function to fetch SCSS content
const fetchSCSS = async (path) => {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Failed to fetch SCSS file: ${response.statusText}`);
  }
  return await response.text();
};

const PageCover = React.forwardRef(({ imageSource, children }, ref) => {
  return (
    <div className="cover" ref={ref} data-density="hard">
      <img
        src={imageSource}
        alt="Cover"
        style={{ width: "100%", height: "100%" }}
      />
      <div>
        <h2>{children}</h2>
      </div>
    </div>
  );
});

const Page = React.forwardRef((props, ref) => {
  const { backgroundImage, pagebg } = props;
  const pageStyle = {
    backgroundImage: backgroundImage ? `url(${backgroundImage})` : "none",
    width: "100%",
    height: "100%",
  };
  return (
    <div className="page" ref={ref} style={pageStyle}>
      <div
        style={{ backgroundImage: `url(${pagebg})` }}
        className="page-content"
      >
        <div className="logo-container">
          <img className="logo" src={props.logo} alt="logo" />
        </div>
        <div className="page-main-content">{props.children}</div>
      </div>
      <div className="page-footer bg-danger">
        {props.number > 0 && (
          <div className="page-number-container rounded-circle bg-light text-dark">
            <p className="page-number">{props.number}</p>
          </div>
        )}
      </div>
    </div>
  );
});

function MyBook(props) {
  const [pages, setPages] = useState([]);
  const pageFlipRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  useEffect(() => {
    setPages(props.pages);

    const handleKeyDown = (event) => {
      switch (event.keyCode) {
        case 37: // Left arrow key
          if (pageFlipRef.current && pageFlipRef.current.pageFlip()) {
            pageFlipRef.current.pageFlip().flipPrev();
          }
          break;
        case 39: // Right arrow key
          if (pageFlipRef.current && pageFlipRef.current.pageFlip()) {
            pageFlipRef.current.pageFlip().flipNext();
          }
          break;
        default:
          break;
      }
    };

    const handleHashChange = () => {
      scrollToPageFromHash();
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("hashchange", handleHashChange);

    const checkFlipBookInstance = () => {
      if (pageFlipRef.current && pageFlipRef.current.pageFlip()) {
        scrollToPageFromHash();
      } else {
        requestAnimationFrame(checkFlipBookInstance);
      }
    };

    checkFlipBookInstance();

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [props.pages]);

  useEffect(() => {
    if (window.screen.width <= 767) {
      setZoomLevel(0.8);
    }
  }, []);

  useEffect(() => {
    const applySCSS = async () => {
      try {
        const scssPath = props.pages.scssPath;
        if (scssPath) {
          const scssContent = await fetchSCSS(scssPath);
          const styleElement = document.createElement("style");
          styleElement.textContent = scssContent;
          document.head.appendChild(styleElement);
        }
      } catch (error) {
        console.error("Failed to load SCSS file", error);
      }
    };

    applySCSS();
  }, [props.pages.scssPath]);

  const scrollToPageFromHash = () => {
    const hash = window.location.hash;
    if (hash && hash.includes("page-")) {
      const pageNumber = parseInt(hash.substring(hash.indexOf("-") + 1));
      if (
        !isNaN(pageNumber) &&
        pageFlipRef.current &&
        pageFlipRef.current.pageFlip()
      ) {
        pageFlipRef.current.pageFlip().turnToPage(pageNumber);
      }
    }
  };

  const handlePageLinkClick = (pageNumber) => (event) => {
    event.preventDefault();
    if (pageFlipRef.current && pageFlipRef.current.pageFlip()) {
      pageFlipRef.current.pageFlip().turnToPage(pageNumber);
      window.location.hash = `page-${pageNumber}`;
    } else {
      console.error("pageFlip instance is not available.");
    }
  };

  const handleFlip = (event) => {
    const currentPageIndex = event.data;
    console.log(`Flipped to page index: ${currentPageIndex}`);
    window.location.hash = `page-${currentPageIndex}`;
  };

  return (
    <div className="bg-container">
      <div className="happy-logo-top-left">
        <a
          href="https://happynenapu.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            className="happy-logo"
            src="images/happynenapulogo.png"
            alt="Happy-Logo"
          />
        </a>
      </div>
      <div className="album-web">
        <Zoom zoomLevel={zoomLevel} setZoomLevel={setZoomLevel} />
        
        <HTMLFlipBook
          width={450}
          height={585}
          minWidth={315}
          maxWidth={1000}
          minHeight={420}
          maxHeight={1350}
          showCover={true}
          startZIndex={1}
          flippingTime={1000}
          style={{
            margin: "0 auto",
            transform: `scale(${zoomLevel})`,
            transition: "transform 0.5s ease-in-out",
          }}
          maxShadowOpacity={0.5}
          maxOpacity={10}
          ref={pageFlipRef}
          onFlip={handleFlip}
        >
          <PageCover imageSource={props.pages.coverimage}></PageCover>
          {props.pages.data.map((page, index) => {
            const Component = React.lazy(() => import(`./${page.component}`));
            return (
              <Page
                number={page.pageNumber}
                subbgimg={page.subbgimg}
                backgroundImage={page.backgroundImage}
                key={index}
                logo={props.pages.logo}
                pagebg={!page.subbgimg ? props.pages.pagebg : page.subbgimg}
              >
                <div id={`page-${index}`}>
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <Component {...page.data} />
                  </React.Suspense>
                </div>
              </Page>
            );
          })}
          <PageCover imageSource={props.pages.coverimgback}></PageCover>
        </HTMLFlipBook>
      </div>
    </div>
  );
}

export default MyBook;
